import { MouseEvent, useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { getIDFromUrl } from '@rossum/api-client';
import { Message } from '@rossum/api-client/shared';
import { useCopyToClipboard } from '@rossum/components/copy-to-clipboard/useCopyToClipboard';
import { Dialog, Stack, Typography } from '@rossum/ui/material';
import {
  isFormulaMessageDetail,
  isRuleMessageDetail,
} from '../../../components/DatapointMessage/helpers';
import HTMLMessage from '../../../components/DatapointMessage/HTMLMessage';
import ActionDialog from '../../../components/UI/ActionDialog';
import ActionDialogActions from '../../../components/UI/ActionDialog/ActionDialogActions';
import { useBackToDocument } from '../../../features/annotation-view/document-sidebar/back-to-document/useBackToDocument';
import { getRulesSettingsDetailPath } from '../../../features/annotation-view/document-sidebar/sidebar-items/utils';
import { extensionDetailPath } from '../../../features/extensions/routes';
import {
  addSecondsToDate,
  convertToUTC,
  formatDateHoursSecondsToQuery,
} from '../../../lib/dateUtils';
import { getIDFromString } from '../../../lib/url';
import { findDatapointById } from '../../../redux/modules/datapoints/navigation/findDatapointIndex';
import { schemaMapSelector } from '../../../redux/modules/schema/schemaMapSelector';
import { isUserAdmin } from '../../../redux/modules/user/selectors';
import { State } from '../../../types/state';
import { activitiesExtensionsPath } from '../../Activities/routes';
import { useFieldsRoute } from './useFieldsRoute';

type RowProps = {
  value: string;
  label: string;
};

const Row = ({ value, label }: RowProps) => (
  <Stack direction="row" gap={2}>
    <Typography variant="subtitle2" align="right" minWidth="25%">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ wordBreak: 'break-word' }}
      component="div"
    >
      <HTMLMessage content={value} />
    </Typography>
  </Stack>
);

const TIME_WINDOW = 60; // time window in seconds for increasing chance to find desired results

type MessageDetailDialogProps = {
  messageDetail: Exclude<Message['detail'], null | undefined>;
  content: string;
  open: boolean;
  onClose: () => void;
  datapointId: Message['id'];
};

const MessageDetailDialogContent = ({
  messageDetail,
  content,
  onClose,
  datapointId,
}: MessageDetailDialogProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { pathname } = useLocation();

  const { triggerBackToDocumentToast } = useBackToDocument();

  const isAdmin = useSelector(isUserAdmin);

  const queueUrl = useSelector((state: State) => state.annotation.queue);
  const queueId = queueUrl ? getIDFromUrl(queueUrl) : null;
  const datapoints = useSelector((state: State) => state.datapoints.content);
  const datapoint = useMemo(() => {
    return findDatapointById(datapoints, Number(datapointId));
  }, [datapoints, datapointId]);

  const schemaMap = useSelector(schemaMapSelector);

  const schema = schemaMap.get(datapoint?.schemaId ?? '');

  const rows = [
    { key: 'annotationId', value: getIDFromString(pathname) },
    { key: 'extensionId', value: messageDetail.hookId },
    { key: 'extensionName', value: messageDetail.hookName },
    { key: 'requestId', value: messageDetail.requestId },
    { key: 'message', value: content },
  ] as const;

  const getTimestampQuery = (timestampString: string) => {
    const timestamp = convertToUTC(new Date(timestampString));
    const before = formatDateHoursSecondsToQuery(
      addSecondsToDate(timestamp, TIME_WINDOW)
    );
    const after = formatDateHoursSecondsToQuery(
      addSecondsToDate(timestamp, -TIME_WINDOW)
    );
    return `&startBefore=${before}&startAfter=${after}`;
  };

  const contentForCopy = rows
    .map(({ key, value }) => `${key}: ${value}`)
    .join('\n');

  const { copyToClipboard } = useCopyToClipboard(contentForCopy);

  const fieldsRoute = useFieldsRoute(datapoint, queueId);

  const rulesRoute =
    queueId && messageDetail.ruleId
      ? getRulesSettingsDetailPath(queueId, messageDetail.ruleId)
      : null;

  const { hookId } = messageDetail;

  const valueSource =
    isFormulaMessageDetail(messageDetail) &&
    !!fieldsRoute &&
    !!schema &&
    'uiConfiguration' in schema
      ? schema.uiConfiguration?.type
      : null;

  const isRuleRelated = isRuleMessageDetail(messageDetail) && !!rulesRoute;

  const showConfirmButton =
    isAdmin &&
    (hookId !== null ||
      valueSource === 'formula' ||
      valueSource === 'reasoning' ||
      isRuleRelated);

  const logsLink =
    hookId !== null
      ? `${activitiesExtensionsPath()}?hook=${hookId}${
          messageDetail.timestamp
            ? getTimestampQuery(messageDetail.timestamp)
            : ''
        }`
      : null;

  const confirmLink =
    fieldsRoute && (valueSource === 'formula' || valueSource === 'reasoning')
      ? fieldsRoute
      : isRuleRelated
        ? rulesRoute
        : hookId
          ? extensionDetailPath(hookId)
          : null;

  const moreActions = showConfirmButton
    ? R.pipe(
        [
          logsLink !== null && {
            localizationKey:
              'components.documentValidation.sidebar.exceptionalError.modal.button.log' as const,
            to: logsLink,
            onClick: (e: MouseEvent) => {
              if (e.metaKey || e.ctrlKey) return;

              triggerBackToDocumentToast();
            },
          },
          {
            localizationKey:
              'components.documentValidation.sidebar.exceptionalError.modal.copyToClipboard' as const,
            onClick: copyToClipboard,
          },
        ],
        R.filter(R.isTruthy)
      )
    : undefined;

  return (
    <ActionDialog
      dialogTitle={intl.formatMessage({
        id: 'components.documentValidation.sidebar.exceptionalError.modal.title',
      })}
      onCancel={onClose}
      dialogColor="primary"
      dialogActions={
        <ActionDialogActions
          hideConfirmButton={!showConfirmButton}
          confirmButtonProps={{
            href: confirmLink ?? undefined,
            onClick: e => {
              if (e.metaKey || e.ctrlKey || !confirmLink) return;

              e.preventDefault();
              history.push(confirmLink);
              triggerBackToDocumentToast();
            },
            color: 'primary',
          }}
          secondaryButtonProps={
            moreActions ? undefined : { onClick: copyToClipboard }
          }
          confirmButtonText={intl.formatMessage({
            id:
              valueSource === 'formula'
                ? 'components.documentValidation.sidebar.exceptionalError.modal.button.schemaField'
                : valueSource === 'reasoning'
                  ? 'components.documentValidation.sidebar.exceptionalError.modal.button.reasoning'
                  : isRuleRelated
                    ? `components.documentValidation.sidebar.exceptionalError.modal.button.rule`
                    : `components.documentValidation.sidebar.exceptionalError.modal.button.extension`,
          })}
          secondaryButtonText={intl.formatMessage({
            id: `components.documentValidation.sidebar.exceptionalError.modal.copyToClipboard`,
          })}
          cancelButtonText={intl.formatMessage({
            id: `components.documentValidation.sidebar.exceptionalError.modal.button.close`,
          })}
          moreActions={moreActions}
        />
      }
      dataCy="exceptional-error-modal-cancel-button"
    >
      <Stack>
        {rows.map(({ key, value }) => {
          if (!value) return null;

          return (
            <Row
              key={key}
              value={`${value}`}
              label={intl.formatMessage({
                id: `components.documentValidation.sidebar.exceptionalError.modal.label.${key}`,
              })}
            />
          );
        })}
        {!isAdmin && (
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mt: 2 }}
          >
            {intl.formatMessage({
              id: 'components.documentValidation.sidebar.exceptionalError.modal.helpMessage',
            })}
          </Typography>
        )}
      </Stack>
    </ActionDialog>
  );
};

const MessageDetailDialog = (props: MessageDetailDialogProps) => {
  return (
    <Dialog
      open={props.open}
      PaperProps={{
        sx: { width: 480, minHeight: 220, position: 'fixed' },
        elevation: 2,
      }}
      onClick={e => e.stopPropagation()}
      onClose={props.onClose}
      sx={{ transition: 'smooth' }}
    >
      <MessageDetailDialogContent {...props} />
    </Dialog>
  );
};

export default MessageDetailDialog;
